import type { CaseOutcome, GameState } from './types';
import { createInitialState } from './reducer';

const SAVE_KEY = 'hotei:v4:case-001';
const PREVIOUS_SAVE_KEYS = [
  'echo-court:v4:case-001',
  'echo-court:v3:case-001',
  'echo-court:v2:case-001',
  'echo-court:v1:case-001',
];
const HISTORY_KEY = 'hotei:v4:history';
const PREVIOUS_HISTORY_KEYS = [
  'echo-court:v4:history',
  'echo-court:v3:history',
  'echo-court:v2:history',
  'echo-court:v1:history',
];

function migrateState(saved: Partial<GameState> & { schemaVersion?: number }): GameState {
  const fresh = createInitialState();
  return {
    ...fresh,
    ...saved,
    schemaVersion: 4,
    chapter: saved.chapter ?? fresh.chapter,
    questionedCharacterIds: saved.questionedCharacterIds ?? [],
    pressureByStatement: saved.pressureByStatement ?? {},
    courtEvent: null,
    notice: null,
    profile: { ...fresh.profile, ...(saved.profile ?? {}) },
    audio: { ...fresh.audio, ...(saved.audio ?? {}) },
    yuragi: saved.yuragi ?? fresh.yuragi,
    yuragiClockMs: saved.yuragiClockMs ?? 0,
  };
}

export function loadGame(): GameState | null {
  try {
    const saved = localStorage.getItem(SAVE_KEY) ??
      PREVIOUS_SAVE_KEYS.map((key) => localStorage.getItem(key)).find(Boolean);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as Partial<GameState> & { schemaVersion?: number };
    return migrateState(parsed);
  } catch {
    return null;
  }
}

export function saveGame(state: GameState): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  } catch {
    // The game remains fully playable when storage is unavailable.
  }
}

export function clearGame(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
    PREVIOUS_SAVE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore unavailable storage.
  }
}

export function loadHistory(): CaseOutcome[] {
  try {
    const history = JSON.parse(
      localStorage.getItem(HISTORY_KEY) ??
      PREVIOUS_HISTORY_KEYS.map((key) => localStorage.getItem(key)).find(Boolean) ??
      '[]',
    ) as CaseOutcome[];
    return Array.isArray(history) ? history.slice(0, 8) : [];
  } catch {
    return [];
  }
}

export function rememberOutcome(outcome: CaseOutcome): void {
  try {
    const history = loadHistory().filter((item) => item.resultHash !== outcome.resultHash);
    localStorage.setItem(HISTORY_KEY, JSON.stringify([outcome, ...history].slice(0, 8)));
  } catch {
    // Ignore unavailable storage.
  }
}
